import React from "react";
import { AuditEvidence } from "@/components/AuditEvidence";
import { GOAL_CATS, fmtGoal, STATUS_STYLE, QUAL_STYLE } from "@/lib/auditGoals";

const GRID = "grid grid-cols-[1fr_auto] md:grid-cols-[minmax(0,2.2fr)_100px_90px_90px_110px_minmax(0,2fr)] gap-x-4 gap-y-1 items-center px-5 py-3 border-t border-slate-100";
const Badge = ({ text, cls, testId }) => <span className={`px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${cls}`} data-testid={testId}>{text}</span>;

function GoalRow({ g, periodId, onChanged }) {
  const soll = g.target == null ? "—" : `${g.direction === "max" ? "≤ " : "≥ "}${fmtGoal(g.target, g.unit)}`;
  return (
    <div className={GRID} data-testid={`fb-goal-${g.id}`}>
      <div className="text-sm text-slate-900">{g.label}</div>
      <div className="md:hidden"><Badge text={g.status} cls={STATUS_STYLE[g.status]} /></div>
      <div className="text-xs md:text-sm font-mono text-slate-500"><span className="md:hidden">Ziel </span>{soll}</div>
      <div className="text-xs md:text-sm font-mono text-slate-900" data-testid={`fb-goal-actual-${g.id}`}><span className="md:hidden">Ist </span>{fmtGoal(g.actual, g.unit)}</div>
      <div className="text-xs md:text-sm font-mono text-slate-400" title={g.previous_label ? `Vorjahr: ${g.previous_label}` : "Kein passendes Ziel im vorherigen Zeitraum"} data-testid={`fb-goal-prev-${g.id}`}><span className="md:hidden">Vorjahr </span>{fmtGoal(g.previous_actual, g.unit)}</div>
      <div className="hidden md:block"><Badge text={g.status} cls={STATUS_STYLE[g.status]} testId={`fb-goal-status-${g.id}`} /></div>
      <div className="text-[11px] text-slate-500 col-span-2 md:col-span-1">
        <div>{g.note || "—"}</div>
        <AuditEvidence periodId={periodId} goal={g} onChanged={onChanged} />
      </div>
    </div>
  );
}

const ColHead = () => (
  <div className="hidden md:grid grid-cols-[minmax(0,2.2fr)_100px_90px_90px_110px_minmax(0,2fr)] gap-x-4 px-5 py-2 text-[10px] font-bold uppercase tracking-overline text-slate-400">
    <span>Ziel</span><span>Soll</span><span>Ist</span><span>Vorjahr</span><span>Status</span><span>Quelle / Nachweise</span>
  </div>
);

const Head = ({ n, title }) => (
  <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-baseline gap-3">
    <span className="font-heading text-xl font-light text-slate-400">{n}</span>
    <span className="font-heading text-xl font-light tracking-tighter text-slate-900">{title}</span>
  </div>
);

export default function AuditFormblatt({ r, onChanged }) {
  return (
    <div className="space-y-6" data-testid="audit-formblatt">
      {GOAL_CATS.map(([cat, title], i) => {
        const goals = (r.custom_goals || []).filter((g) => g.category === cat);
        const qual = cat === "unternehmen" ? r.qualitative : [];
        return (
          <div key={cat} className="border border-slate-200 bg-white" data-testid={`fb-${cat}`}>
            <Head n={i + 1} title={title} />
            {goals.length > 0 && <ColHead />}
            {goals.map((g) => <GoalRow key={g.id} g={g} periodId={r.period.period_id} onChanged={onChanged} />)}
            {qual.map((q) => (
              <div key={q.id} className={GRID} data-testid={`fb-qual-${q.id}`}>
                <div className="text-sm text-slate-900 md:col-span-4">{q.text}</div>
                <div><Badge text={q.status} cls={QUAL_STYLE[q.status] || QUAL_STYLE.offen} /></div>
                <div className="text-[11px] text-slate-500 col-span-2 md:col-span-1">{q.note || "—"}</div>
              </div>
            ))}
            {!goals.length && !qual.length && <div className="px-5 py-4 text-sm text-slate-400" data-testid={`fb-empty-${cat}`}>Noch keine Ziele eingetragen – über „Bearbeiten" hinzufügen.</div>}
          </div>
        );
      })}
    </div>
  );
}
